import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { flow } from 'lodash-es';
import classnames from 'classnames';
import Col from 'reactstrap/lib/Col';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import { TrackingHandler } from 'client/tracking/handler';
import { TrackingMap } from 'client/tracking/maps/consumer-reviews/consumer-reviews';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import {
  ConsumerReviewsEntities,
  ConsumerReviewsModel,
  ConsumerReviewsPaths,
} from 'client/data/models/consumer-reviews';
import { buildVehiclePath, buildVehicleStylePath, VehicleEntities, VehicleModel } from 'client/data/models/vehicle-v2';
import { DEALERLESS_OEM } from 'site-modules/shared/constants/oem';
import { EventToolbox } from 'client/utils/event-toolbox';
import { ConsumerReviewsParams } from 'site-modules/shared/utils/consumer-reviews/params';
import { buildSpecificStyleDisplayVehicle } from 'site-modules/shared/utils/core-page/display-name-builders';
import { isUsed as isUsedPubState } from 'site-modules/shared/utils/publication-states';
import { PAGE_EVENTS } from 'client/constants/page-events';
import { ConsumerReviewsIntro } from 'site-modules/consumer-reviews/components/consumer-reviews-intro/consumer-reviews-intro';
import { ReviewsList } from 'site-modules/consumer-reviews/components/reviews-list/reviews-list';
import { MRECT_WIRED_CUSTOM_SIZE } from 'site-modules/shared/components/ad-unit/context/sizes';
import { PricingCarouselFooter } from 'site-modules/shared/components/core-page/pricing-carousel/pricing-carousel-footer';
import { SponsoredCars } from 'site-modules/shared/components/core-page/sponsored-cars/sponsored-cars';
import { MinHeightWrapper } from 'site-modules/shared/components/min-height-wrapper/min-height-wrapper';
import { MrectNative } from 'site-modules/shared/components/native-ad/mrect/mrect-native';
import { NativeVideoAd } from 'site-modules/shared/components/native-ad/video/video-native-ad';
import { PageLinksWidget } from 'site-modules/shared/components/page-links-widget/page-links-widget';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';

import './consumer-reviews.scss';

export const PAGE_DISPLAY_NAME = 'Consumer Reviews';
export const URL_PATH = 'consumer-reviews';

function ConsumerReviewsUI({
  params: propsParams,
  vehicle = null,
  style = null,
  isMobile = false,
  vehicleFilter = null,
  reviewsFilter = null,
}) {
  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    TrackingHandler.tryAddMap(TrackingMap);

    function removeLoading() {
      setLoading(false);
    }

    EventToolbox.on(PAGE_EVENTS.PAGE_LOAD, removeLoading);

    return () => {
      EventToolbox.off(PAGE_EVENTS.PAGE_LOAD, removeLoading);
    };
  }, []);

  const addLoading = useCallback(() => {
    setLoading(true);
  }, []);

  if (!vehicleFilter || !vehicle) {
    return (
      <main id="main-content" tabIndex={-1} className="consumer-reviews">
        <Container>Loading&hellip;</Container>
      </main>
    );
  }

  const params = {
    ...propsParams,
    isUsed: isUsedPubState(vehicle.pubStates),
    isDealerlessOem: DEALERLESS_OEM.includes(vehicle.make.slug),
  };

  const displayVehicle = buildSpecificStyleDisplayVehicle({ makeModelSubmodelYear: vehicle, style, params });

  return (
    <main id="main-content" tabIndex={-1} className={classnames('consumer-reviews', { loading: isLoading })}>
      <Container className="pb-1_5">
        <ConsumerReviewsIntro
          params={params}
          vehicle={vehicle}
          displayVehicle={displayVehicle}
          reviewsFilter={reviewsFilter}
          vehicleFilter={vehicleFilter}
          addLoading={addLoading}
          isMobile={isMobile}
        />
        <Row>
          <Col xs={12} lg={8} className="mb-1_5">
            <ReviewsList
              vehicle={vehicle}
              isMobile={isMobile}
              reviewsFilter={reviewsFilter}
              vehicleFilter={vehicleFilter}
              addLoading={addLoading}
              isUsed={params.isUsed}
            />
          </Col>
          <Col lg={4} className="hidden-lg-down">
            <NativeVideoAd useMinHeight wiredOnly wrapperClass="mt-xl-1_5" />
            <MrectNative
              wrapperClass="mrect-wrapper separator"
              wiredBreakpoints={{ lg: true, xl: true }}
              wiredOnly
              useMinHeight
            />
            <MrectNative
              position={'2'}
              wiredBreakpoints={{ lg: true, xl: true }}
              wiredOnly
              addCustomSizesWired={MRECT_WIRED_CUSTOM_SIZE}
            />
          </Col>
          <Col xs={12} className="mt-0 mt-lg-2">
            <Experiment name="ads-12460_dfp" showDefault>
              <Recipe name="ctrl" isDefault>
                <MinHeightWrapper componentType="SPONSORED_CARS">
                  <SponsoredCars />
                </MinHeightWrapper>
              </Recipe>
              <Recipe name="chal">
                <MinHeightWrapper
                  componentType="SPONSORED_CARS_STACKED"
                  style={isMobile ? null : { maxWidth: '744px' }}
                >
                  <SponsoredCars showStackedAds />
                </MinHeightWrapper>
              </Recipe>
            </Experiment>
          </Col>
          {!params.isUsed && (
            <Col xs={12} className="mb-1_5">
              <MinHeightWrapper componentType="SUGGESTED_PRICING_CAROUSEL">
                <PricingCarouselFooter
                  params={params}
                  srpParams={{ ...params, inventorytype: 'new' }}
                  disableTrademarkCharacter
                />
              </MinHeightWrapper>
            </Col>
          )}

          <MrectNative wrapperClass="mb-2" position={'2'} wiredBreakpoints={{ md: true }} wiredOnly />
        </Row>

        <PageLinksWidget divideSections={false} title={`Related ${displayVehicle} info`} />
      </Container>
    </main>
  );
}

ConsumerReviewsUI.propTypes = {
  params: ConsumerReviewsParams.isRequired,
  vehicle: VehicleEntities.MakeModelSubmodelYear,
  style: VehicleEntities.Style,
  isMobile: PropTypes.bool,
  reviewsFilter: ConsumerReviewsEntities.ReviewsFilter,
  vehicleFilter: ConsumerReviewsEntities.VehicleFilter,
};

const mapStateToProps = state => ({
  isMobile: state.mobile,
});

export const ConsumerReviews = flow(
  component =>
    connectToModel(component, {
      vehicle: bindToPath(({ params }) => buildVehiclePath(params), VehicleModel),
      style: bindToPath(({ params }) => (params.styleId ? buildVehicleStylePath(params) : ''), VehicleModel),
      reviewsFilter: bindToPath(ConsumerReviewsPaths.getConsumerReviewsFilterPath(), ConsumerReviewsModel),
      vehicleFilter: bindToPath(ConsumerReviewsPaths.getVehicleFilterPath(), ConsumerReviewsModel),
    }),
  connect(mapStateToProps)
)(ConsumerReviewsUI);
